import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, EmptyState, fmtDateTime } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import PhotoUpload from "@/components/PhotoUpload";
import { toast } from "sonner";
import { OFFLINE_TEMPERATURES_ENABLED } from "@/lib/offline/featureFlags";
import { connectivity } from "@/lib/offline/connectivity";
import { recordTemperatureOffline } from "@/lib/offline/offlineTemperature";
import { Plus, Thermometer, AlertTriangle, CheckCircle2, Settings2, Snowflake, Play, Square, Timer, Flame, Truck, Boxes, Sunrise, Moon, ChevronRight, MoreVertical, CircleSlash, Check, Pencil, Trash2, Microwave, Link2 } from "lucide-react";
import { UNIT_TYPE_LABELS, UNIT_TYPES, SKIP_REASONS, SKIP_LABELS, isOutOfRange, roundOutcomeTone } from "@/lib/rounds";

const genOpId = () => (crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

function elapsed(startIso, nowMs) {
  const secs = Math.max(0, Math.floor((nowMs - new Date(startIso).getTime()) / 1000));
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

function CoolingPanel({ cooling, venues, canRecord, onChanged }) {
  const [nowMs, setNowMs] = useState(Date.now());
  const [startModal, setStartModal] = useState(false);
  const [finish, setFinish] = useState(null);
  useEffect(() => { const t = setInterval(() => setNowMs(Date.now()), 1000); return () => clearInterval(t); }, []);

  const active = cooling.filter((c) => c.status === "cooling");
  const recent = cooling.filter((c) => c.status === "completed").slice(0, 4);

  return (
    <div className="cc-card p-5 mb-6" data-testid="cooling-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Snowflake className="text-brand" size={18} /><span className="font-display font-bold text-slate-800">Cook &amp; cool timers</span></div>
        {canRecord && <Button size="sm" className="bg-brand hover:bg-brand-hover rounded-lg" data-testid="start-cooling-btn" onClick={() => setStartModal(true)}><Play size={14} className="mr-1" /> Start cooling</Button>}
      </div>
      {active.length === 0 && recent.length === 0 && <p className="text-sm text-slate-400">No cooling logs. Start one when you pull hot food to chill.</p>}
      <div className="space-y-2">
        {active.map((c) => {
          const mins = Math.floor((nowMs - new Date(c.start_at).getTime()) / 60000);
          const over = mins > (c.limit_minutes || 90);
          return (
            <div key={c.cooling_id} className={`flex items-center justify-between rounded-xl border p-3 ${over ? "border-red-200 bg-red-50/50" : "border-slate-200"}`} data-testid={`cooling-${c.cooling_id}`}>
              <div className="flex items-center gap-3">
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${over ? "bg-red-100 text-red-600" : "bg-brand-light text-brand-dark"}`}><Timer size={18} /></div>
                <div><div className="font-semibold text-slate-800">{c.food}</div>
                  <div className="text-xs text-slate-500">from {c.start_temperature}°C · target ≤{c.target_temperature}°C in {c.limit_minutes}m</div></div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`font-mono font-bold ${over ? "text-red-600" : "text-slate-700"}`} data-testid={`cooling-elapsed-${c.cooling_id}`}>{elapsed(c.start_at, nowMs)}</span>
                {canRecord && <Button size="sm" variant="outline" className="rounded-lg" data-testid={`finish-cooling-${c.cooling_id}`} onClick={() => setFinish(c)}><Square size={13} className="mr-1" /> Finish</Button>}
              </div>
            </div>
          );
        })}
        {recent.map((c) => (
          <div key={c.cooling_id} className="flex items-center justify-between rounded-xl border border-slate-100 p-3 text-sm" data-testid={`cooling-done-${c.cooling_id}`}>
            <span className="text-slate-600">{c.food} — {c.end_temperature}°C in {c.duration_minutes}m</span>
            <StatusBadge status={c.passed ? "passed" : "exception"} />
          </div>
        ))}
      </div>
      {startModal && <StartCoolingModal venues={venues} onClose={() => setStartModal(false)} onSaved={() => { setStartModal(false); onChanged(); }} />}
      {finish && <FinishCoolingModal log={finish} onClose={() => setFinish(null)} onSaved={() => { setFinish(null); onChanged(); }} />}
    </div>
  );
}

function StartCoolingModal({ venues, onClose, onSaved }) {
  const [f, setF] = useState({ food: "", start_temperature: "", target_temperature: 8, limit_minutes: 90, venue_id: "" });
  const save = async () => {
    if (!f.food) return toast.error("Food name required");
    await api.post("/cooling/start", { food: f.food, start_temperature: f.start_temperature === "" ? null : Number(f.start_temperature), target_temperature: Number(f.target_temperature), limit_minutes: Number(f.limit_minutes), venue_id: f.venue_id || null, venue_name: (venues.find((v) => v.venue_id === f.venue_id) || {}).name || "" });
    toast.success("Cooling timer started"); onSaved();
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="start-cooling-modal">
        <DialogHeader><DialogTitle>Start cooling</DialogTitle><DialogDescription className="sr-only">Start a cook-and-cool timer to monitor food chilling within the safe time limit.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs font-semibold text-slate-600">Food</Label><Input value={f.food} onChange={(e) => setF({ ...f, food: e.target.value })} data-testid="cooling-food" className="mt-1.5" placeholder="e.g. Beef stew" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs font-semibold text-slate-600">Start °C</Label><Input type="number" value={f.start_temperature} onChange={(e) => setF({ ...f, start_temperature: e.target.value })} data-testid="cooling-start-temp" className="mt-1.5" /></div>
            <div><Label className="text-xs font-semibold text-slate-600">Target ≤°C</Label><Input type="number" value={f.target_temperature} onChange={(e) => setF({ ...f, target_temperature: e.target.value })} data-testid="cooling-target" className="mt-1.5" /></div>
            <div><Label className="text-xs font-semibold text-slate-600">Limit min</Label><Input type="number" value={f.limit_minutes} onChange={(e) => setF({ ...f, limit_minutes: e.target.value })} data-testid="cooling-limit" className="mt-1.5" /></div>
          </div>
          {venues.length > 0 && (
            <div><Label className="text-xs font-semibold text-slate-600">Venue</Label>
              <Select value={f.venue_id} onValueChange={(v) => setF({ ...f, venue_id: v })}><SelectTrigger data-testid="cooling-venue" className="mt-1.5"><SelectValue placeholder="Optional venue" /></SelectTrigger>
                <SelectContent>{venues.map((v) => <SelectItem key={v.venue_id} value={v.venue_id}>{v.name}</SelectItem>)}</SelectContent></Select></div>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-cooling-start" className="bg-brand hover:bg-brand-hover">Start timer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FinishCoolingModal({ log, onClose, onSaved }) {
  const [endTemp, setEndTemp] = useState("");
  const [comments, setComments] = useState("");
  const [nowMs, setNowMs] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const target = log.target_temperature ?? 8;
  const limitMinutes = log.limit_minutes ?? 90;
  const elapsedMinutes = Math.max(0, Math.round((nowMs - new Date(log.start_at).getTime()) / 60000));
  const temperatureBreach = endTemp !== "" && Number(endTemp) > target;
  const timeBreach = elapsedMinutes > limitMinutes;
  // Backend pass/fail requires BOTH target temperature and time limit. Keep the UI
  // on the same rule so every failed cooling log exposes the mandatory explanation box.
  const breach = temperatureBreach || timeBreach;
  const save = async () => {
    if (endTemp === "") return toast.error("Enter the final temperature");
    if (breach && !comments.trim()) return toast.error("Cooling breach — a comment is required");
    try { const { data } = await api.post(`/cooling/${log.cooling_id}/finish`, { end_temperature: Number(endTemp), comments }); toast[data.passed ? "success" : "warning"](data.passed ? `Cooled safely in ${data.duration_minutes}m` : "Cooling breach — management alerted"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="finish-cooling-modal">
        <DialogHeader><DialogTitle>Finish cooling — {log.food}</DialogTitle><DialogDescription className="sr-only">Record the final temperature for this cooling log; a breach above the target requires a comment.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs font-semibold text-slate-600">Final temperature (°C)</Label><Input type="number" value={endTemp} onChange={(e) => setEndTemp(e.target.value)} data-testid="cooling-end-temp" className="mt-1.5 text-2xl font-bold h-14" placeholder="0.0" /></div>
          {breach && <div className="bg-red-50 border border-red-200 rounded-xl p-3" data-testid="cooling-breach-warning">
            <div className="flex items-center gap-2 text-red-700 text-sm font-semibold mb-2">
              <AlertTriangle size={16} />
              {temperatureBreach && timeBreach
                ? `Above target ≤${target}°C and over ${limitMinutes} min — explanation required`
                : temperatureBreach
                  ? `Above target ≤${target}°C — explanation required`
                  : `Cooling time exceeded ${limitMinutes} min — explanation required`}
            </div>
            <Textarea autoFocus value={comments} onChange={(e) => setComments(e.target.value)} data-testid="cooling-comment" placeholder="What happened, and what corrective action did you take?" /></div>}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-cooling-finish" className="bg-brand hover:bg-brand-hover">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TYPES = [
  { v: "delivery", l: "Delivery" }, { v: "cold_storage", l: "Cold storage" },
  { v: "cooking", l: "Cooking" }, { v: "cooling", l: "Cooling" }, { v: "reheating", l: "Reheating" },
  { v: "fresh_delivery", l: "Fresh delivery" }, { v: "frozen_delivery", l: "Frozen delivery" },
];

function RoundsPanel({ data, canRecord, canManageEquip, onStart, onOpen, onManageEquip }) {
  const due = data?.due || [];
  const inProgress = data?.in_progress || [];
  const today = data?.today || [];

  if (!data?.units_configured) {
    return (
      <div className="cc-card p-5 mb-6" data-testid="rounds-panel-empty">
        <div className="flex items-center gap-2 mb-2"><Boxes className="text-brand" size={18} /><span className="font-display font-bold text-slate-800">Cold-storage rounds</span></div>
        <p className="text-sm text-slate-500 mb-3">Set up your fridges & freezers to run fast opening and closing temperature rounds.</p>
        {canManageEquip && <Button size="sm" className="bg-brand hover:bg-brand-hover rounded-lg" data-testid="setup-equipment-btn" onClick={onManageEquip}><Plus size={14} className="mr-1" /> Add cold-storage equipment</Button>}
      </div>
    );
  }

  return (
    <div className="cc-card p-5 mb-6" data-testid="rounds-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Boxes className="text-brand" size={18} /><span className="font-display font-bold text-slate-800">Cold-storage rounds</span></div>
        {canManageEquip && <button onClick={onManageEquip} data-testid="manage-equipment-link" className="text-xs font-semibold text-slate-500 hover:text-brand flex items-center gap-1"><Settings2 size={13} /> Equipment</button>}
      </div>

      {(due.length > 0 || inProgress.length > 0) ? (
        <div className="grid sm:grid-cols-2 gap-2.5">
          {inProgress.map((r) => (
            <button key={r.round_id} onClick={() => onOpen(r)} data-testid={`continue-round-${r.round_type}`}
              className="text-left rounded-xl border border-amber-200 bg-amber-50/60 p-3.5 flex items-center justify-between hover:border-amber-300 transition-colors">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center">{r.round_type === "opening" ? <Sunrise size={18} /> : <Moon size={18} />}</div>
                <div><div className="font-semibold text-slate-800 capitalize">{r.round_type} round</div>
                  <div className="text-xs text-amber-700">In progress · {r.summary.recorded}/{r.summary.expected} done</div></div>
              </div>
              <ChevronRight className="text-amber-500" size={18} />
            </button>
          ))}
          {canRecord && due.map((d) => (
            <button key={`${d.venue_id}-${d.round_type}`} onClick={() => onStart(d)} data-testid={`start-round-${d.round_type}`}
              className="text-left rounded-xl border border-slate-200 p-3.5 flex items-center justify-between hover:border-brand transition-colors">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-brand-light text-brand-dark flex items-center justify-center">{d.round_type === "opening" ? <Sunrise size={18} /> : <Moon size={18} />}</div>
                <div><div className="font-semibold text-slate-800 capitalize">{d.round_type} round</div>
                  <div className="text-xs text-slate-500">{d.venue_name || "All areas"} · {d.units} unit{d.units !== 1 ? "s" : ""}</div></div>
              </div>
              <span className="text-xs font-semibold text-brand flex items-center gap-1"><Play size={12} /> Start</span>
            </button>
          ))}
        </div>
      ) : (
        <p className="text-sm text-brand-dark flex items-center gap-2"><CheckCircle2 size={16} /> All rounds for today are done.</p>
      )}

      {today.filter((t) => t.status === "completed").length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5" data-testid="rounds-today-done">
          {today.filter((t) => t.status === "completed").map((t) => (
            <div key={t.round_id} className="flex items-center justify-between text-xs">
              <span className="text-slate-600 capitalize flex items-center gap-1.5">{t.round_type === "opening" ? <Sunrise size={12} /> : <Moon size={12} />} {t.round_type} · {t.venue_name || "All areas"}</span>
              <span className={`font-semibold ${roundOutcomeTone(t.outcome)}`}>{t.recorded} measured{t.skipped ? ` · ${t.skipped} skipped` : ""}{t.failed ? ` · ${t.failed} exception` : ""}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UnitRow({ unit, item, canRecord, onRecord, onSkip }) {
  const [mode, setMode] = useState(null); // null | "record" | "skip"
  const [temp, setTemp] = useState("");
  const [comment, setComment] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const outOfRange = isOutOfRange(unit, temp);

  if (item) {
    const skipped = item.state === "skipped";
    return (
      <div className={`rounded-xl border p-3 flex items-center justify-between ${skipped ? "border-amber-200 bg-amber-50/50" : item.passed ? "border-brand-light bg-brand-light/30" : "border-red-200 bg-red-50/50"}`} data-testid={`round-unit-${unit.unit_id}`}>
        <div className="flex items-center gap-3">
          <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${skipped ? "bg-amber-100 text-amber-600" : item.passed ? "bg-brand-light text-brand-dark" : "bg-red-100 text-red-600"}`}>
            {skipped ? <CircleSlash size={16} /> : item.passed ? <Check size={16} /> : <AlertTriangle size={16} />}
          </div>
          <div><div className="font-semibold text-slate-800 text-sm">{unit.unit_name}</div>
            <div className="text-xs text-slate-500">{skipped ? `Not measured · ${SKIP_LABELS[item.skip_reason] || item.skip_reason}` : `${item.temperature}°C · ${item.passed ? "within range" : "exception"}`}</div></div>
        </div>
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400" data-testid={`round-unit-state-${unit.unit_id}`}>{skipped ? "Skipped" : item.passed ? "Recorded" : "Exception"}</span>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 p-3" data-testid={`round-unit-${unit.unit_id}`}>
      <div className="flex items-center justify-between">
        <div><div className="font-semibold text-slate-800 text-sm">{unit.unit_name}</div>
          <div className="text-xs text-slate-400">{UNIT_TYPE_LABELS[unit.unit_type] || unit.unit_type} · target {unit.minimum ?? "–"} to {unit.maximum ?? "∞"} °C</div></div>
        {mode === null && canRecord && (
          <div className="flex items-center gap-2">
            <button onClick={() => setMode("skip")} data-testid={`round-skip-btn-${unit.unit_id}`} className="text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-500 hover:border-amber-300">Can't check</button>
            <button onClick={() => setMode("record")} data-testid={`round-record-btn-${unit.unit_id}`} className="text-xs px-2.5 py-1.5 rounded-lg bg-brand text-white font-medium">Record</button>
          </div>
        )}
      </div>

      {mode === "record" && (
        <div className="mt-3 space-y-2">
          <Input type="number" autoFocus value={temp} onChange={(e) => setTemp(e.target.value)} data-testid={`round-temp-input-${unit.unit_id}`} className="text-xl font-bold h-12" placeholder="0.0 °C" />
          {outOfRange && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-2.5" data-testid={`round-exception-${unit.unit_id}`}>
              <div className="flex items-center gap-1.5 text-red-700 text-xs font-semibold mb-1.5"><AlertTriangle size={13} /> Outside range — corrective action required</div>
              <Textarea value={comment} onChange={(e) => setComment(e.target.value)} data-testid={`round-comment-${unit.unit_id}`} placeholder="What did you find and do?" className="text-sm" />
            </div>
          )}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1" onClick={() => { setMode(null); setTemp(""); setComment(""); }}>Cancel</Button>
            <Button size="sm" className="flex-1 bg-brand hover:bg-brand-hover" data-testid={`round-save-${unit.unit_id}`} disabled={busy || temp === "" || (outOfRange && !comment.trim())}
              onClick={async () => { setBusy(true); try { await onRecord(unit, Number(temp), comment); } finally { setBusy(false); } }}>Save</Button>
          </div>
        </div>
      )}

      {mode === "skip" && (
        <div className="mt-3 space-y-2">
          <Select value={reason} onValueChange={setReason}><SelectTrigger data-testid={`round-skip-reason-${unit.unit_id}`}><SelectValue placeholder="Why can't this be measured?" /></SelectTrigger>
            <SelectContent>{SKIP_REASONS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent></Select>
          {reason === "other" && <Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid={`round-skip-note-${unit.unit_id}`} placeholder="Explain why it was not measured" className="text-sm" />}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1" onClick={() => { setMode(null); setReason(""); setNote(""); }}>Cancel</Button>
            <Button size="sm" className="flex-1 bg-amber-500 hover:bg-amber-600 text-white" data-testid={`round-skip-save-${unit.unit_id}`} disabled={busy || !reason || (reason === "other" && !note.trim())}
              onClick={async () => { setBusy(true); try { await onSkip(unit, reason, note); } finally { setBusy(false); } }}>Mark not measured</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function RoundModal({ round: initial, canRecord, onClose, onChanged }) {
  const [round, setRound] = useState(initial);
  const [completing, setCompleting] = useState(false);
  const itemsByUnit = Object.fromEntries((round.items || []).map((it) => [it.unit_id, it]));
  const summary = round.summary || {};
  const outstanding = summary.outstanding ?? 0;

  const record = async (unit, temperature, comments) => {
    try { const { data } = await api.post(`/temperature-rounds/${round.round_id}/reading`, { unit_id: unit.unit_id, temperature, comments, operation_id: genOpId() });
      setRound(data.round); onChanged?.(); toast[data.item.passed ? "success" : "warning"](data.item.passed ? "Reading saved ✓" : "Exception recorded — management alerted");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const skip = async (unit, skip_reason, skip_note) => {
    try { const { data } = await api.post(`/temperature-rounds/${round.round_id}/skip`, { unit_id: unit.unit_id, skip_reason, skip_note });
      setRound(data.round); onChanged?.(); toast.info(`${unit.unit_name} marked not measured`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not skip"); }
  };
  const complete = async () => {
    setCompleting(true);
    try { await api.post(`/temperature-rounds/${round.round_id}/complete`); toast.success("Round completed"); onChanged?.(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not complete"); }
    finally { setCompleting(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="round-modal" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 capitalize">{round.round_type === "opening" ? <Sunrise size={18} className="text-brand" /> : <Moon size={18} className="text-brand" />} {round.round_type} round</DialogTitle>
          <DialogDescription className="sr-only">Record or skip each cold-storage unit, then complete the round.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between text-xs text-slate-500 mb-1" data-testid="round-progress">
          <span>{round.venue_name || "All areas"}</span>
          <span>{summary.recorded ?? 0} measured · {summary.skipped ?? 0} skipped · {outstanding} to go</span>
        </div>
        <div className="space-y-2">
          {(round.expected_units || []).map((u) => (
            <UnitRow key={u.unit_id} unit={u} item={itemsByUnit[u.unit_id]} canRecord={canRecord} onRecord={record} onSkip={skip} />
          ))}
        </div>
        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose} data-testid="round-close-btn">Close</Button>
          <Button className="bg-brand hover:bg-brand-hover" data-testid="round-complete-btn" disabled={completing || outstanding > 0} onClick={complete}>
            {outstanding > 0 ? `${outstanding} unit${outstanding !== 1 ? "s" : ""} left` : "Complete round"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EquipmentModal({ venues, onClose, onChanged }) {
  const [units, setUnits] = useState([]);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState({ name: "", unit_type: "fridge", minimum: "", maximum: "", venue_id: "" });
  const load = async () => { const { data } = await api.get("/cold-storage-units"); setUnits(data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!f.name.trim()) return toast.error("Enter a unit name");
    const body = { name: f.name.trim(), unit_type: f.unit_type, minimum: f.minimum === "" ? null : Number(f.minimum), maximum: f.maximum === "" ? null : Number(f.maximum) };
    if (f.venue_id) body.venue_id = f.venue_id;
    try {
      if (editing) await api.put(`/cold-storage-units/${editing}`, body);
      else await api.post("/cold-storage-units", body);
      toast.success(editing ? "Unit updated" : "Unit added"); setF({ name: "", unit_type: "fridge", minimum: "", maximum: "", venue_id: "" }); setEditing(null); load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const del = async (u) => { try { await api.delete(`/cold-storage-units/${u.unit_id}`); toast.success("Removed"); load(); onChanged?.(); } catch (e) { toast.error("Could not remove"); } };
  const edit = (u) => { setEditing(u.unit_id); setF({ name: u.name, unit_type: u.unit_type, minimum: u.minimum ?? "", maximum: u.maximum ?? "", venue_id: u.venue_id || "" }); };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="equipment-modal" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Cold-storage equipment</DialogTitle><DialogDescription className="sr-only">Add, edit or remove the fridges and freezers measured in opening and closing rounds.</DialogDescription></DialogHeader>
        <div className="space-y-2 max-h-56 overflow-y-auto" data-testid="equipment-list">
          {units.length === 0 && <p className="text-sm text-slate-400">No units yet — add your first fridge or freezer below.</p>}
          {units.map((u) => (
            <div key={u.unit_id} className="flex items-center justify-between border rounded-lg p-2.5 text-sm" data-testid={`equip-${u.unit_id}`}>
              <div><span className="font-medium">{u.name}</span> <span className="text-slate-400">· {UNIT_TYPE_LABELS[u.unit_type] || u.unit_type} · {u.minimum ?? "–"} to {u.maximum ?? "∞"}°C</span></div>
              <div className="flex items-center gap-1.5">
                <button onClick={() => edit(u)} data-testid={`equip-edit-${u.unit_id}`} className="text-slate-400 hover:text-brand p-1"><Pencil size={14} /></button>
                <button onClick={() => del(u)} data-testid={`equip-del-${u.unit_id}`} className="text-slate-400 hover:text-red-500 p-1"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
        <div className="border-t pt-3 space-y-2">
          <Input placeholder="Unit name (e.g. Walk-in fridge)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="equip-name" />
          <div className="grid grid-cols-3 gap-2">
            <Select value={f.unit_type} onValueChange={(v) => setF({ ...f, unit_type: v })}><SelectTrigger data-testid="equip-type"><SelectValue /></SelectTrigger><SelectContent>{UNIT_TYPES.map((t) => <SelectItem key={t} value={t}>{UNIT_TYPE_LABELS[t]}</SelectItem>)}</SelectContent></Select>
            <Input placeholder="Min °C" type="number" value={f.minimum} onChange={(e) => setF({ ...f, minimum: e.target.value })} data-testid="equip-min" />
            <Input placeholder="Max °C" type="number" value={f.maximum} onChange={(e) => setF({ ...f, maximum: e.target.value })} data-testid="equip-max" />
          </div>
          {venues.length > 0 && (
            <Select value={f.venue_id || "none"} onValueChange={(v) => setF({ ...f, venue_id: v === "none" ? "" : v })}><SelectTrigger data-testid="equip-venue"><SelectValue placeholder="All areas" /></SelectTrigger>
              <SelectContent><SelectItem value="none">All areas</SelectItem>{venues.map((v) => <SelectItem key={v.venue_id} value={v.venue_id}>{v.name}</SelectItem>)}</SelectContent></Select>
          )}
          <div className="flex gap-2">
            {editing && <Button variant="outline" className="flex-1" onClick={() => { setEditing(null); setF({ name: "", unit_type: "fridge", minimum: "", maximum: "", venue_id: "" }); }}>Cancel edit</Button>}
            <Button onClick={save} data-testid="equip-save" className="flex-1 bg-brand hover:bg-brand-hover">{editing ? "Save changes" : "Add unit"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const FILTERS = [
  { v: "all", l: "All" }, { v: "cold_storage", l: "Cold storage" }, { v: "cooking", l: "Cooking" },
  { v: "cooling", l: "Cooling" }, { v: "reheating", l: "Reheat" }, { v: "delivery", l: "Delivery" }, { v: "exceptions", l: "Exceptions" },
];

export default function Temperatures() {
  const { has, moduleEnabled } = useAuth();
  const [records, setRecords] = useState([]);
  const [thresholds, setThresholds] = useState([]);
  const [cooling, setCooling] = useState([]);
  const [venues, setVenues] = useState([]);
  const [roundsData, setRoundsData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [venueFilter, setVenueFilter] = useState("all");
  const [modal, setModal] = useState(false);
  const [cookModal, setCookModal] = useState(false);
  const [reheatModal, setReheatModal] = useState(false);
  const [threshModal, setThreshModal] = useState(false);
  const [equipModal, setEquipModal] = useState(false);
  const [activeRound, setActiveRound] = useState(null);
  const navigate = useNavigate();

  const canRecord = has("create_temperature");
  const canManageEquip = has("edit_temperature");

  const load = async () => {
    const [r, t, c, v, rd] = await Promise.all([
      api.get("/temperatures"), api.get("/temperature-thresholds"), api.get("/cooling"),
      api.get("/me/sites"), api.get("/temperature-rounds"),
    ]);
    setRecords(r.data); setThresholds(t.data); setCooling(c.data); setVenues(v.data.sites || []); setRoundsData(rd.data);
  };
  useEffect(() => { load(); }, []);

  const startRound = async (due) => {
    try { const body = { round_type: due.round_type }; if (due.venue_id) body.venue_id = due.venue_id;
      const { data } = await api.post("/temperature-rounds/start", body); setActiveRound(data); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start round"); }
  };
  const openRound = async (r) => {
    try { const { data } = await api.get(`/temperature-rounds/${r.round_id}`); setActiveRound(data); }
    catch (e) { toast.error("Could not open round"); }
  };

  const filtered = (filter === "all" ? records : filter === "exceptions" ? records.filter((r) => !r.passed) : records.filter((r) => r.record_type === filter))
    .filter((r) => venueFilter === "all" || r.venue_id === venueFilter);

  return (
    <div>
      <PageHeader title="Temperature Controls" subtitle="Run your opening & closing rounds and keep signed temperature records." testid="temperatures-header"
        action={<div className="flex gap-2 flex-wrap">
          {canRecord && <Button className="bg-brand hover:bg-brand-hover rounded-xl" data-testid="record-temp-btn" onClick={() => setModal(true)}><Plus size={18} className="mr-1" /> Record</Button>}
          {canRecord && <Button variant="outline" className="rounded-xl" data-testid="cook-temp-btn" onClick={() => setCookModal(true)}><Flame size={16} className="mr-1" /> Cook</Button>}
          {canRecord && <Button variant="outline" className="rounded-xl" data-testid="reheat-temp-btn" onClick={() => setReheatModal(true)}><Microwave size={16} className="mr-1" /> Reheat</Button>}
          {moduleEnabled("haccp") && <Button variant="outline" className="rounded-xl" data-testid="sous-vide-link-btn" onClick={() => navigate("/sous-vide")}><Timer size={16} className="mr-1" /> Sous-vide records</Button>}
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="outline" className="rounded-xl px-2.5" data-testid="temp-more-menu"><MoreVertical size={18} /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canRecord && <DropdownMenuItem data-testid="delivery-temp-btn" onClick={() => navigate("/deliveries?receive=1")}><Truck size={15} className="mr-2" /> Record delivery</DropdownMenuItem>}
              {canManageEquip && <DropdownMenuItem data-testid="manage-equipment" onClick={() => setEquipModal(true)}><Boxes size={15} className="mr-2" /> Cold-storage equipment</DropdownMenuItem>}
              {canManageEquip && <DropdownMenuItem data-testid="manage-thresholds" onClick={() => setThreshModal(true)}><Settings2 size={15} className="mr-2" /> Thresholds</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>} />

      {has("view_temperature") && <RoundsPanel data={roundsData} canRecord={canRecord} canManageEquip={canManageEquip}
        onStart={startRound} onOpen={openRound} onManageEquip={() => setEquipModal(true)} />}

      {has("view_temperature") && <CoolingPanel cooling={cooling} venues={venues} canRecord={canRecord} onChanged={load} />}

      {venues.length > 0 && (
        <div className="mb-3 flex gap-2 flex-wrap items-center" data-testid="venue-filter">
          <span className="text-xs font-semibold text-slate-500 uppercase">Venue</span>
          <button onClick={() => setVenueFilter("all")} className={`text-sm px-3 py-1 rounded-full ${venueFilter === "all" ? "bg-slate-800 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>All</button>
          {venues.map((v) => <button key={v.venue_id} onClick={() => setVenueFilter(v.venue_id)} data-testid={`venue-filter-${v.venue_id}`} className={`text-sm px-3 py-1 rounded-full ${venueFilter === v.venue_id ? "bg-slate-800 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{v.name}</button>)}
        </div>
      )}

      <div className="flex gap-2 flex-wrap mb-5" data-testid="temp-filters">
        {FILTERS.map((t) => (
          <button key={t.v} onClick={() => setFilter(t.v)} data-testid={`filter-${t.v}`}
            className={`text-sm px-3.5 py-1.5 rounded-full font-medium transition-colors ${filter === t.v ? "bg-brand text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{t.l}</button>
        ))}
      </div>

      {filtered.length === 0 ? <EmptyState icon={Thermometer} title="No temperature records for this period." /> :
        <div className="space-y-2" data-testid="temp-list">
          {filtered.map((r) => (
            <div key={r.temp_id} className={`cc-card p-4 flex items-center justify-between ${!r.passed ? "border-red-200 bg-red-50/40" : ""}`} data-testid={`temp-${r.temp_id}`}>
              <div className="flex items-center gap-3">
                <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${r.passed ? "bg-brand-light" : "bg-red-100"}`}>
                  {r.passed ? <CheckCircle2 className="text-brand-dark" size={20} /> : <AlertTriangle className="text-red-500" size={20} />}
                </div>
                <div>
                  <div className="font-semibold text-slate-800">{r.item || FILTERS.find((t) => t.v === r.record_type)?.l || r.record_type} · <span className={r.passed ? "text-brand-dark" : "text-red-600"}>{r.temperature}{r.unit}</span></div>
                  <div className="text-xs text-slate-500 capitalize">{r.threshold_name ? `${r.threshold_name} · ` : ""}{(r.record_type || "").replace("_", " ")} · Recorded by {r.created_by_name} · {fmtDateTime(r.created_at)}{r.source_type === "caterctrl_offline" ? " · Recorded offline" : ""}</div>
                  {r.source_cooling_record_id && <div className="text-[11px] text-brand-dark mt-0.5 flex items-center gap-1" data-testid={`reheat-linked-${r.temp_id}`}><Link2 size={11} /> Linked to cooling · Cook → Cool → Reheat</div>}
                  {r.comments && <div className="text-xs text-slate-500 italic mt-0.5">"{r.comments}"</div>}
                </div>
              </div>
              <StatusBadge status={r.passed ? "passed" : "exception"} />
            </div>
          ))}
        </div>}

      {modal && <RecordModal thresholds={thresholds} venues={venues} onClose={() => setModal(false)} onSaved={() => { setModal(false); load(); }} />}
      {cookModal && <CookModal thresholds={thresholds} venues={venues} onClose={() => setCookModal(false)} onSaved={() => { setCookModal(false); load(); }} />}
      {reheatModal && <ReheatModal venues={venues} onClose={() => setReheatModal(false)} onSaved={() => { setReheatModal(false); load(); }} />}
      {threshModal && <ThresholdModal thresholds={thresholds} onClose={() => setThreshModal(false)} onChanged={load} />}
      {equipModal && <EquipmentModal venues={venues} onClose={() => setEquipModal(false)} onChanged={load} />}
      {activeRound && <RoundModal round={activeRound} canRecord={canRecord} onClose={() => { setActiveRound(null); load(); }} onChanged={load} />}
    </div>
  );
}

function RecordModal({ thresholds, venues, onClose, onSaved }) {
  const [thresholdId, setThresholdId] = useState(thresholds[0]?.threshold_id || "");
  const [venueId, setVenueId] = useState("");
  const [item, setItem] = useState("");
  const [temp, setTemp] = useState("");
  const [comments, setComments] = useState("");
  const [commentError, setCommentError] = useState("");
  const [photo, setPhoto] = useState(null);
  const exceptionRef = useRef(null);
  const th = thresholds.find((t) => t.threshold_id === thresholdId);
  const type = th?.record_type || "cold_storage";
  const outOfRange = temp !== "" && th && ((th.minimum != null && Number(temp) < th.minimum) || (th.maximum != null && Number(temp) > th.maximum));

  useEffect(() => {
    if (outOfRange) exceptionRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    else setCommentError("");
  }, [outOfRange]);

  const save = async () => {
    if (temp === "") return toast.error("Enter a temperature");
    if (outOfRange && !comments.trim()) {
      setCommentError("A corrective-action comment is required before an out-of-range temperature can be saved.");
      exceptionRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      return;
    }
    try {
      const body = { record_type: type, item, temperature: Number(temp), comments, photo_file_id: photo };
      if (venueId) { body.venue_id = venueId; body.venue_name = (venues.find((v) => v.venue_id === venueId) || {}).name || ""; }
      if (th) { body.minimum = th.minimum; body.maximum = th.maximum; body.unit = th.unit; body.threshold_name = th.name; }
      // Offline path (feature-flagged): only when the flag is ON and genuinely offline.
      // Flag OFF or online → unchanged online submission below.
      if (OFFLINE_TEMPERATURES_ENABLED && connectivity.get() === "offline") {
        try {
          await recordTemperatureOffline(body); // durable local write FIRST
          toast.success("Saved on this device — waiting to sync"); // only after persistence succeeds
          onSaved();
        } catch (e) {
          if (e?.name === "OfflineStorageUnavailableError") { /* NOT-SAVED toast shown by OfflineErrorToaster */ }
          else if (e?.message === "offline_auth_expired") toast.error("Reconnect to continue — your offline window has expired");
          else if (e?.message === "offline_site_not_authorised") toast.error("You are not authorised to record at this site");
          else toast.error("Not saved — please try again");
        }
        return;
      }
      const { data } = await api.post("/temperatures", body);
      toast[data.passed ? "success" : "warning"](data.passed ? "Temperature saved ✓" : "Exception recorded — management alerted");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="temp-modal">
        <DialogHeader><DialogTitle>Record temperature</DialogTitle><DialogDescription className="sr-only">Record a temperature reading against a control point; out-of-range readings require a corrective-action comment.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div><Label className="text-xs font-semibold text-slate-600">Control point</Label>
            <Select value={thresholdId} onValueChange={setThresholdId}><SelectTrigger data-testid="temp-type" className="mt-1.5"><SelectValue placeholder="Select control point" /></SelectTrigger>
              <SelectContent>{thresholds.map((t) => <SelectItem key={t.threshold_id} value={t.threshold_id}>{t.name} · {TYPES.find((x) => x.v === t.record_type)?.l}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs font-semibold text-slate-600">Item / unit</Label><Input value={item} onChange={(e) => setItem(e.target.value)} data-testid="temp-item" className="mt-1.5" placeholder="e.g. Walk-in fridge" /></div>
          {venues.length > 0 && (
            <div><Label className="text-xs font-semibold text-slate-600">Venue</Label>
              <Select value={venueId} onValueChange={setVenueId}><SelectTrigger data-testid="temp-venue" className="mt-1.5"><SelectValue placeholder="Optional venue" /></SelectTrigger>
                <SelectContent>{venues.map((v) => <SelectItem key={v.venue_id} value={v.venue_id}>{v.name}</SelectItem>)}</SelectContent></Select></div>
          )}
          <div>
            <Label className="text-xs font-semibold text-slate-600">Temperature ({th?.unit || "°C"})</Label>
            <Input type="number" value={temp} onChange={(e) => setTemp(e.target.value)} data-testid="temp-value" className="mt-1.5 text-2xl font-bold h-14" placeholder="0.0" />
            {th && <div className="text-xs text-slate-400 mt-1">Required: {th.minimum ?? "–"} to {th.maximum ?? "∞"} {th.unit}</div>}
          </div>
          {outOfRange && (
            <div ref={exceptionRef} className="bg-red-50 border border-red-200 rounded-xl p-3" data-testid="temp-exception-warning">
              <div className="flex items-center gap-2 text-red-700 text-sm font-semibold mb-2"><AlertTriangle size={16} /> Outside required range — corrective action required</div>
              <p className="text-xs text-red-600/90 mb-2">This reading is outside the {th?.name || "control point"} guideline ({th?.minimum ?? "–"} to {th?.maximum ?? "∞"} {th?.unit || "°C"}). Record what you found and the action you took — a food-safety record can't be saved without it.</p>
              <Textarea autoFocus value={comments} onChange={(e) => { setComments(e.target.value); if (commentError) setCommentError(""); }} data-testid="temp-comment" placeholder="What happened and what action did you take?" className={commentError ? "border-red-400 focus-visible:ring-red-400" : ""} />
              {commentError && <p className="text-xs font-medium text-red-600 mt-1.5" data-testid="temp-comment-error">{commentError}</p>}
            </div>
          )}
          {!outOfRange && <div><Label className="text-xs font-semibold text-slate-600">Comment (optional)</Label><Textarea value={comments} onChange={(e) => setComments(e.target.value)} data-testid="temp-comment" className="mt-1.5" /></div>}
          {type === "delivery" && <div><Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Invoice photo</Label><PhotoUpload value={photo} onChange={setPhoto} label="Take Photo of invoice" testid="temp-photo" /></div>}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-temp" className="bg-brand hover:bg-brand-hover">Save record</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ThresholdModal({ thresholds, onClose, onChanged }) {
  const [f, setF] = useState({ name: "", record_type: "cold_storage", minimum: "", maximum: "", unit: "°C" });
  const add = async () => {
    if (!f.name) return toast.error("Name required");
    await api.post("/temperature-thresholds", { ...f, minimum: f.minimum === "" ? null : Number(f.minimum), maximum: f.maximum === "" ? null : Number(f.maximum) });
    toast.success("Threshold added"); setF({ name: "", record_type: "cold_storage", minimum: "", maximum: "", unit: "°C" }); onChanged();
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="threshold-modal">
        <DialogHeader><DialogTitle>Temperature thresholds</DialogTitle><DialogDescription className="sr-only">View, add or remove temperature control points and their acceptable minimum and maximum ranges.</DialogDescription></DialogHeader>
        <div className="space-y-2 max-h-52 overflow-y-auto">
          {thresholds.map((t) => (
            <div key={t.threshold_id} className="flex items-center justify-between text-sm border rounded-lg p-2.5">
              <span className="font-medium">{t.name} <span className="text-slate-400 capitalize">({t.record_type.replace("_", " ")})</span></span>
              <span className="flex items-center gap-2">{t.minimum ?? "–"} to {t.maximum ?? "∞"} {t.unit}
                <button data-testid={`del-threshold-${t.threshold_id}`} onClick={async () => { await api.delete(`/temperature-thresholds/${t.threshold_id}`); onChanged(); }} className="text-red-400">✕</button></span>
            </div>
          ))}
        </div>
        <div className="border-t pt-3 space-y-2">
          <Input placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="threshold-name" />
          <div className="grid grid-cols-3 gap-2">
            <Select value={f.record_type} onValueChange={(v) => setF({ ...f, record_type: v })}><SelectTrigger data-testid="threshold-type"><SelectValue /></SelectTrigger><SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent></Select>
            <Input placeholder="Min" type="number" value={f.minimum} onChange={(e) => setF({ ...f, minimum: e.target.value })} data-testid="threshold-min" />
            <Input placeholder="Max" type="number" value={f.maximum} onChange={(e) => setF({ ...f, maximum: e.target.value })} data-testid="threshold-max" />
          </div>
          <Button onClick={add} data-testid="add-threshold" className="w-full bg-brand hover:bg-brand-hover">Add threshold</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CookModal({ thresholds, venues, onClose, onSaved }) {
  const cookThs = thresholds.filter((t) => t.record_type === "cooking");
  const [thresholdId, setThresholdId] = useState(cookThs[0]?.threshold_id || "");
  const [venueId, setVenueId] = useState("");
  const [item, setItem] = useState("");
  const [temp, setTemp] = useState("");
  const [intent, setIntent] = useState("serve_now");
  const [comments, setComments] = useState("");
  const th = cookThs.find((t) => t.threshold_id === thresholdId);
  const outOfRange = temp !== "" && th && ((th.minimum != null && Number(temp) < th.minimum) || (th.maximum != null && Number(temp) > th.maximum));
  const save = async () => {
    if (!item.trim()) return toast.error("Enter the food/item");
    if (temp === "") return toast.error("Enter the core temperature");
    if (outOfRange && !comments.trim()) return toast.error("Corrective action required for out-of-range cooking");
    try {
      const vn = (venues.find((v) => v.venue_id === venueId) || {}).name || "";
      const body = { record_type: "cooking", item, temperature: Number(temp), comments, cooking_intent: intent };
      if (venueId) { body.venue_id = venueId; body.venue_name = vn; }
      if (th) { body.minimum = th.minimum; body.maximum = th.maximum; body.unit = th.unit; body.threshold_name = th.name; }
      const { data } = await api.post("/temperatures", body);
      if (intent === "cool_later") {
        await api.post("/cooling/start", { food: item, start_temperature: Number(temp), target_temperature: 8, limit_minutes: 90, venue_id: venueId || null, venue_name: vn });
        toast.success(data.passed ? "Cooking recorded — cooling timer started" : "Exception recorded — cooling timer started");
      } else {
        toast[data.passed ? "success" : "warning"](data.passed ? "Cooked & served — recorded ✓" : "Exception recorded — management alerted");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="cook-modal">
        <DialogHeader><DialogTitle>Cooking temperature</DialogTitle><DialogDescription className="sr-only">Record a cooking/core temperature and choose whether the food is served immediately or cooled for later.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          {cookThs.length > 0 && <div><Label className="text-xs font-semibold text-slate-600">Control point (optional)</Label>
            <Select value={thresholdId} onValueChange={setThresholdId}><SelectTrigger data-testid="cook-threshold" className="mt-1.5"><SelectValue placeholder="Optional cooking control point" /></SelectTrigger>
              <SelectContent>{cookThs.map((t) => <SelectItem key={t.threshold_id} value={t.threshold_id}>{t.name}</SelectItem>)}</SelectContent></Select></div>}
          <div><Label className="text-xs font-semibold text-slate-600">Food / item</Label><Input value={item} onChange={(e) => setItem(e.target.value)} data-testid="cook-item" className="mt-1.5" placeholder="e.g. Chicken breast" /></div>
          {venues.length > 0 && <div><Label className="text-xs font-semibold text-slate-600">Venue</Label>
            <Select value={venueId} onValueChange={setVenueId}><SelectTrigger data-testid="cook-venue" className="mt-1.5"><SelectValue placeholder="Optional venue" /></SelectTrigger>
              <SelectContent>{venues.map((v) => <SelectItem key={v.venue_id} value={v.venue_id}>{v.name}</SelectItem>)}</SelectContent></Select></div>}
          <div><Label className="text-xs font-semibold text-slate-600">Core temperature (°C)</Label>
            <Input type="number" value={temp} onChange={(e) => setTemp(e.target.value)} data-testid="cook-temp" className="mt-1.5 text-2xl font-bold h-14" placeholder="0.0" />
            {th && <div className="text-xs text-slate-400 mt-1">Configured target: {th.minimum ?? "–"} to {th.maximum ?? "∞"} {th.unit}</div>}</div>
          <div>
            <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">After cooking</Label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setIntent("serve_now")} data-testid="cook-serve-now" className={`min-h-[44px] rounded-xl border text-sm font-medium ${intent === "serve_now" ? "bg-brand text-white border-brand" : "border-slate-200 text-slate-600"}`}>Serve / use now</button>
              <button type="button" onClick={() => setIntent("cool_later")} data-testid="cook-cool-later" className={`min-h-[44px] rounded-xl border text-sm font-medium ${intent === "cool_later" ? "bg-brand text-white border-brand" : "border-slate-200 text-slate-600"}`}>Cool for later</button>
            </div>
          </div>
          {outOfRange && <div className="bg-red-50 border border-red-200 rounded-xl p-3" data-testid="cook-exception">
            <div className="flex items-center gap-2 text-red-700 text-sm font-semibold mb-2"><AlertTriangle size={16} /> Outside configured range — corrective action required</div>
            <Textarea autoFocus value={comments} onChange={(e) => setComments(e.target.value)} data-testid="cook-comment" placeholder="What did you find and what action did you take?" /></div>}
          {!outOfRange && <div><Label className="text-xs font-semibold text-slate-600">Note (optional)</Label><Textarea value={comments} onChange={(e) => setComments(e.target.value)} data-testid="cook-comment" className="mt-1.5" /></div>}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-cook" className="bg-brand hover:bg-brand-hover">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ReheatModal({ venues, onClose, onSaved }) {
  const [suggestions, setSuggestions] = useState([]);
  const [source, setSource] = useState(null); // selected cooling record or null = different product
  const [item, setItem] = useState("");
  const [temp, setTemp] = useState("");
  const [venueId, setVenueId] = useState("");
  const [comments, setComments] = useState("");
  const [busy, setBusy] = useState(false);
  const MIN = 75;
  const below = temp !== "" && Number(temp) < MIN;

  useEffect(() => { api.get("/reheat/cooling-suggestions").then((r) => setSuggestions(r.data || [])).catch(() => setSuggestions([])); }, []);

  const pick = (c) => { setSource(c); setItem(c.food || ""); if (c.venue_id) setVenueId(c.venue_id); };
  const useDifferent = () => { setSource(null); setItem(""); };

  const save = async () => {
    if (!item.trim()) return toast.error("Enter the product / item");
    if (temp === "") return toast.error("Enter the reheating temperature");
    if (below && !comments.trim()) return toast.error("Below 75°C — a corrective-action comment is required");
    setBusy(true);
    try {
      const vn = (venues.find((v) => v.venue_id === venueId) || {}).name || "";
      const body = { item, temperature: Number(temp), comments };
      if (venueId) { body.venue_id = venueId; body.venue_name = vn; }
      if (source) body.source_cooling_record_id = source.cooling_id;
      const { data } = await api.post("/reheat", body);
      toast[data.passed ? "success" : "warning"](data.passed ? "Reheat recorded ✓" : "Reheat exception — management alerted");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="reheat-modal" className="max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Microwave size={18} className="text-brand" /> Reheat temperature</DialogTitle>
          <DialogDescription className="sr-only">Record a reheating temperature; optionally link it to a recent cooling record, and record corrective action if below the required minimum.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">From a recent cooling record (optional)</Label>
            {suggestions.length === 0 ? (
              <p className="text-xs text-slate-400" data-testid="reheat-no-suggestions">No recent cooling records — record a different product below.</p>
            ) : (
              <div className="space-y-2 max-h-44 overflow-y-auto" data-testid="reheat-suggestions">
                {suggestions.map((c) => (
                  <button key={c.cooling_id} type="button" onClick={() => pick(c)} data-testid={`reheat-suggestion-${c.cooling_id}`}
                    className={`w-full text-left rounded-xl border p-2.5 transition-colors ${source?.cooling_id === c.cooling_id ? "border-brand bg-brand-light/40" : "border-slate-200 hover:border-brand"}`}>
                    <div className="flex items-center justify-between gap-2 min-w-0">
                      <span className="font-semibold text-sm text-slate-800 truncate">{c.food || "Cooled item"}</span>
                      <span className={`text-xs font-medium shrink-0 ${c.passed ? "text-brand-dark" : "text-red-600"}`}>{c.end_temperature}°C</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{c.venue_name ? `${c.venue_name} · ` : ""}{c.duration_minutes != null ? `${c.duration_minutes} min · ` : ""}{fmtDateTime(c.end_at)}{c.cooled_by_name ? ` · ${c.cooled_by_name}` : ""}</div>
                  </button>
                ))}
              </div>
            )}
            <button type="button" onClick={useDifferent} data-testid="reheat-different-product" className={`mt-2 text-xs underline ${source ? "text-brand-dark" : "text-slate-400"}`}>Different product / not from a previous cooling record</button>
          </div>
          <div><Label className="text-xs font-semibold text-slate-600">Product / item</Label><Input value={item} onChange={(e) => { setItem(e.target.value); }} data-testid="reheat-item" className="mt-1.5" placeholder="e.g. Beef lasagne" /></div>
          {venues.length > 0 && (
            <div><Label className="text-xs font-semibold text-slate-600">Venue</Label>
              <Select value={venueId} onValueChange={setVenueId}><SelectTrigger data-testid="reheat-venue" className="mt-1.5"><SelectValue placeholder="Optional venue" /></SelectTrigger>
                <SelectContent>{venues.map((v) => <SelectItem key={v.venue_id} value={v.venue_id}>{v.name}</SelectItem>)}</SelectContent></Select></div>
          )}
          <div>
            <Label className="text-xs font-semibold text-slate-600">Reheating temperature (°C)</Label>
            <Input type="number" value={temp} onChange={(e) => setTemp(e.target.value)} data-testid="reheat-temp" className="mt-1.5 text-2xl font-bold h-14" placeholder="0.0" />
            <div className="text-xs text-slate-400 mt-1">Must reach at least {MIN}°C</div>
          </div>
          {below && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3" data-testid="reheat-exception">
              <div className="flex items-center gap-2 text-red-700 text-sm font-semibold mb-2"><AlertTriangle size={16} /> Below {MIN}°C — corrective action required</div>
              <Textarea autoFocus value={comments} onChange={(e) => setComments(e.target.value)} data-testid="reheat-comment" placeholder="What did you find and what action did you take?" />
            </div>
          )}
          {!below && <div><Label className="text-xs font-semibold text-slate-600">Note (optional)</Label><Textarea value={comments} onChange={(e) => setComments(e.target.value)} data-testid="reheat-comment" className="mt-1.5" /></div>}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={busy} data-testid="save-reheat" className="bg-brand hover:bg-brand-hover">Save reheat</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

